import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, waLink } from "@/lib/api";
import { MapPin, Briefcase, ArrowLeft, MessageCircle, Mail, Globe, Loader2, Star } from "lucide-react";

export default function JobDetail() {
  const { id } = useParams();
  const [vaga, setVaga] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/vagas/${id}`)
      .then((r) => setVaga(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading)
    return (
      <div className="flex justify-center py-32 text-slate-500">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );

  if (notFound || !vaga)
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center">
        <h1 className="font-heading text-3xl font-extrabold">Vaga não encontrada</h1>
        <Link to="/" className="text-emerald-700 font-bold mt-4 inline-block">← Voltar para as vagas</Link>
      </div>
    );

  return (
    <div className="max-w-4xl mx-auto px-5 py-10">
      <Link to="/" data-testid="back-link" className="inline-flex items-center gap-2 text-slate-600 font-bold hover:text-slate-950 mb-6">
        <ArrowLeft size={18} /> Voltar
      </Link>

      <article className="bg-white rounded-3xl p-8 md:p-12 border border-slate-200 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
        <div className="flex flex-wrap gap-2 mb-6">
          <span className="bg-blue-100 text-blue-800 text-sm font-extrabold px-3 py-1 rounded-full flex items-center gap-1">
            <MapPin size={14} /> {vaga.cidade}
          </span>
          <span className="bg-slate-100 text-slate-700 text-sm font-extrabold px-3 py-1 rounded-full">{vaga.tipo}</span>
          {vaga.featured && (
            <span className="bg-emerald-100 text-emerald-800 text-sm font-extrabold px-3 py-1 rounded-full flex items-center gap-1">
              <Star size={14} /> Destaque
            </span>
          )}
        </div>

        <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-950">{vaga.cargo}</h1>
        <p className="text-slate-500 font-bold mt-2 flex items-center gap-2 text-lg">
          <Briefcase size={18} /> {vaga.company_name}
        </p>

        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200">
            <p className="text-slate-500 text-sm font-bold">Salário</p>
            <p className="font-heading text-xl font-extrabold text-slate-950">{vaga.salario}</p>
          </div>
          <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200">
            <p className="text-slate-500 text-sm font-bold">Tipo de contrato</p>
            <p className="font-heading text-xl font-extrabold text-slate-950">{vaga.tipo}</p>
          </div>
        </div>

        <div className="mt-8">
          <h2 className="font-heading text-xl font-extrabold text-slate-950 mb-2">Descrição da vaga</h2>
          <p className="text-slate-700 leading-relaxed whitespace-pre-line">{vaga.descricao}</p>
        </div>

        <h2 className="font-heading text-xl font-extrabold text-slate-950 mt-10 mb-3">Como se candidatar</h2>
        <div className="flex flex-col sm:flex-row flex-wrap gap-3">
          {vaga.whatsapp && (
            <a
              href={waLink(vaga.whatsapp, vaga.cargo)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="apply-whatsapp"
              className="inline-flex items-center justify-center gap-2 bg-emerald-400 text-slate-950 font-extrabold px-7 py-4 rounded-2xl text-lg hover:bg-emerald-500 hover:-translate-y-1 transition-all duration-200"
            >
              <MessageCircle size={22} /> WhatsApp
            </a>
          )}
          {vaga.email && (
            <a
              href={`mailto:${vaga.email}?subject=${encodeURIComponent("Candidatura: " + vaga.cargo)}`}
              data-testid="apply-email"
              className="inline-flex items-center justify-center gap-2 bg-white border-2 border-slate-200 text-slate-800 font-extrabold px-7 py-4 rounded-2xl text-lg hover:border-slate-300 transition-colors"
            >
              <Mail size={22} /> E-mail
            </a>
          )}
          {vaga.url && (
            <a
              href={vaga.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="apply-link"
              className="inline-flex items-center justify-center gap-2 bg-white border-2 border-slate-200 text-slate-800 font-extrabold px-7 py-4 rounded-2xl text-lg hover:border-slate-300 transition-colors"
            >
              <Globe size={22} /> Site da empresa
            </a>
          )}
        </div>
        <p className="text-slate-500 text-sm mt-4">
          Confira sempre os dados da empresa. Nunca pague para se candidatar.
        </p>
      </article>
    </div>
  );
}
